/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useRef, useState } from 'react'
import { Alert, App, Pagination, Select, Skeleton } from 'antd'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { ColDef } from 'ag-grid-community'
import colors from '@sportninja/common/constants/appColors'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  downloadSocialFeedRecipientsCsv,
  getSocialFeedRecipients,
  type RecipientStatus,
  type SocialFeedRecipient,
  type SocialFeedRecipientsParams,
} from '@sportninja/common/services/socialFeed'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import BaseModal from '../../ts-components/BaseModal'
import Button from '../../ts-components/Button'
import DataGrid from '../../ts-components/DataGrid'
import SearchInput from '../../ts-components/SearchInput'
import Text from '../../ts-components/Text'
import { UNJOINED_SWITCH_TITLE } from '../utils/audienceReach'
import { groupLabel, statusLabel } from '../utils/recipientLabels'

const PER_PAGE = 50

/** Bounded so the pager and the footer stay on screen; the rows scroll inside. */
const GRID_HEIGHT = 420

type RecipientsModalProps = {
  isOpen: boolean
  onClose: () => void
  entityId: string | undefined
  entityType: 'organization' | 'team' | 'competition' | null
  entityName?: string
  roleTypes: number[]
  activeCompetition: boolean
  includeUnjoined: boolean
  /**
   * The donut's own "Recipients" total, passed in rather than recomputed, so the two
   * screens cannot drift apart.
   */
  reachTotal: number
  /**
   * The donut's "of N in audience" denominator, from the same derivation.
   *
   * Deliberately NOT channel-scoped and held constant as the switch moves, so it counts
   * populations this list may exclude. Passed in only so the modal can EXPLAIN the
   * difference — never to make the two screens print the same number, which would put a
   * denominator here that this list's own rows contradict.
   */
  audienceTotal: number
}

/**
 * "I would love to be able to know who I'm about to just email."
 *
 * The list behind the number — every recipient, including the people who CANNOT be
 * reached and why. With the sign-up call-to-action out of scope, this screen and the
 * switch that leads to it are the whole of the deliverability mitigation, so it has to be
 * genuinely legible rather than merely present.
 */
export const RecipientsModal: React.FC<RecipientsModalProps> = ({
  isOpen,
  onClose,
  entityId,
  entityType,
  entityName,
  roleTypes,
  activeCompetition,
  includeUnjoined,
  reachTotal,
  audienceTotal,
}) => {
  'use memo'

  const { message } = App.useApp()
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState<RecipientStatus | null>(null)
  const [search, setSearch] = useState('')
  const [isExporting, setIsExporting] = useState(false)

  /*
   * AG Grid renders popups into its own root by default, where the grid's bounds clip
   * them. Inside a fixed modal that also puts them behind the mask, so the body element
   * is handed down as the popup parent.
   */
  const bodyRef = useRef<HTMLDivElement | null>(null)

  const params: SocialFeedRecipientsParams | null = (() => {
    if (!entityId || !entityType) {
      return null
    }

    return {
      entityId,
      entityType,
      roleTypes,
      activeCompetition,
      includeUnjoined,
      page,
      perPage: PER_PAGE,
      status,
      q: search,
    }
  })()

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: queryKeys.socialFeedRecipients.byFingerprint(params),
    queryFn: () =>
      getSocialFeedRecipients(params as SocialFeedRecipientsParams),
    enabled: isOpen && params !== null,
    // The list must agree with the donut it was opened from, so it is never served from
    // a cache that predates a criteria change.
    staleTime: 0,
    // Page, status and search are all part of the query key, so without this every
    // interaction restarts the query with `data` undefined and the grid blanks — the
    // reader loses the table they were reading on each keystroke. Keeps the previous
    // page on screen, dimmed by `isFetching`, until the new one lands.
    placeholderData: keepPreviousData,
  })

  const rows = data?.data ?? []
  const totals = data?.meta?.totals
  const pagination = data?.meta?.pagination

  /*
   * People the panel counts in the audience that this screen accounts for nowhere else:
   * not a row (`totals.all`) and not the unreachable (`totals.no_email`). That leaves the
   * un-joined the current channel selection excludes — the whole of the difference
   * between the donut's denominator and this list.
   *
   * Clamped at zero: on an API build that predates `reach.in_audience` the panel falls
   * back to a locally derived total, and a fallback that undershoots must not surface as
   * a negative count.
   */
  const excludedFromList = Math.max(
    0,
    audienceTotal - (totals?.all ?? 0) - (totals?.no_email ?? 0)
  )

  const statusOptions = (() => {
    const withCount = (label: string, count?: number) =>
      count === undefined ? label : `${label} (${count.toLocaleString()})`

    return [
      { value: '', label: withCount('All statuses', totals?.all) },
      // Ordered by invite standing, because that is the order the reader is asking about:
      // who is on board, who is pending, who never heard from us. Three chips, not four.
      //
      // "No email" is NOT offered, deliberately. People with no address and no account are
      // counted — `totals.no_email` is a real, positive number again, and the line under
      // these controls states it — but they are never NAMED: not in this list, not in the
      // filter, not in the export. Offering the chip would promise rows the server will
      // not return, so the honest surface for that population is the count.
      { value: 'joined', label: withCount('Accepted', totals?.joined) },
      { value: 'invited', label: withCount('Invited', totals?.invited) },
      {
        value: 'not_invited',
        label: withCount('Not Invited', totals?.not_invited),
      },
    ]
  })()

  const columnDefs: ColDef<SocialFeedRecipient>[] = [
    {
      headerName: 'Name',
      flex: 2,
      minWidth: 160,
      valueGetter: ({ data: row }) =>
        [row?.name_first, row?.name_last].filter(Boolean).join(' ') || '—',
    },
    {
      headerName: 'Email',
      field: 'email',
      flex: 3,
      minWidth: 200,
      valueFormatter: ({ value }) => value || '—',
    },
    {
      headerName: 'Role',
      field: 'group',
      flex: 1,
      minWidth: 120,
      valueFormatter: ({ value }) => groupLabel(value),
    },
    {
      headerName: 'Status',
      field: 'status',
      flex: 1,
      minWidth: 140,
      valueFormatter: ({ value }) => statusLabel(value),
    },
  ]

  const handleExport = async () => {
    if (!params) {
      return
    }

    try {
      setIsExporting(true)
      await downloadSocialFeedRecipientsCsv(
        params,
        `${(entityName || 'communication').replace(
          /[^\w-]/g,
          '_'
        )}_recipients.csv`
      )
    } catch (e) {
      message.open({ type: 'error', content: getErrorMessage(e) })
    } finally {
      setIsExporting(false)
    }
  }

  /*
   * Both guard on the value actually changing before resetting the page.
   *
   * SearchInput re-fires its debounced onSearch whenever the callback's identity changes,
   * not only when the text does — its effect depends on the callback. So turning to page 2
   * re-rendered this component, handed SearchInput a fresh callback, and the effect fired
   * onSearch('') straight back, resetting to page 1. The pager looked frozen: the request
   * for page 2 went out and was immediately followed by one for page 1.
   *
   * Guarding on the value fixes it regardless of how often the callback is re-created,
   * which is sturdier than trying to keep the identity stable.
   */
  const handleStatusChange = (value: string) => {
    const next = (value || null) as RecipientStatus | null
    if (next === status) {
      return
    }
    setStatus(next)
    setPage(1)
  }

  const handleSearch = (value: string) => {
    if (value === search) {
      return
    }
    setSearch(value)
    setPage(1)
  }

  // Cold load only — a refetch for page 2 must not blank the table the reader is using.
  const showSkeleton = isLoading && !data

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title='Audience Breakdown'
      desktopWidth='70vw'
      removeBodyPaddingX
      actions={
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            padding: 0 16px;
          `}
        >
          {/* Button stretches to its flex parent, so the width is bounded here. */}
          <div
            css={css`
              min-width: 160px;
            `}
          >
            <Button text='CLOSE' onClick={onClose} variant='secondary' />
          </div>
        </div>
      }
    >
      <div
        ref={bodyRef}
        css={css`
          display: flex;
          flex-direction: column;
          gap: 12px;
        `}
      >
        {/* Controls */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
            padding: 0 16px;
          `}
        >
          <div
            css={css`
              flex: 1;
              min-width: 200px;
            `}
          >
            <SearchInput
              onSearch={handleSearch}
              placeholder='Search name or email'
            />
          </div>

          <Select
            value={status ?? ''}
            onChange={handleStatusChange}
            aria-label='Filter recipients by status'
            options={statusOptions}
            popupMatchSelectWidth={false}
            // Keeps the dropdown inside the modal. Rendered at the document body it
            // lands under the mask, because index.scss pins antd dropdowns to a
            // z-index below this overlay.
            getPopupContainer={(triggerNode) =>
              triggerNode.parentElement || bodyRef.current || document.body
            }
            css={css`
              min-width: 180px;
            `}
          />

          <div
            css={css`
              flex: 0 0 auto;
            `}
          >
            <Button
              text={isExporting ? 'EXPORTING...' : 'EXPORT CSV'}
              onClick={handleExport}
              disabled={isExporting || !params}
              variant='secondary'
            />
          </div>
        </div>

        {/*
          Two independent counts, not a ratio.

          The list names everybody this post can reach; the people it cannot reach are
          stated as a number beside it, because they have no rows to show. Both describe
          the WHOLE audience and never the filtered page: `reachTotal` comes from the
          audience payload, `totals.no_email` is computed over the whole walk, and no
          status filter or search touches either.

          This used to read "{reachTotal} of {totals.all} will be reached". `totals.all` is
          the number of RECIPIENT ROWS, so once the unreachable stopped being rows it
          became the same population as the numerator — the line settled into "740 of 740
          will be reached · 106 cannot be reached", a ratio whose denominator silently
          excluded the very people the second clause is about.
        */}
        {totals && (
          <div
            css={css`
              padding: 0 16px;
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {reachTotal.toLocaleString()}{' '}
              {reachTotal === 1 ? 'recipient' : 'recipients'}
              {totals.no_email
                ? ` · ${totals.no_email.toLocaleString()} cannot be reached`
                : ''}
            </Text>

            {/*
              Why the panel's "of N in audience" is bigger than anything on this screen.

              The two are correct answers to different questions and are NOT reconciled by
              making one match the other: the donut's denominator counts all three
              populations and is deliberately held constant as the switch moves, so the
              reader can compare options against a denominator that does not shift under
              them. This list is the people it can actually name. One click apart, that
              reads as a contradiction unless the gap is stated.

              The gap subtracts BOTH populations already accounted for above — the rows
              (`totals.all`) and the unreachable (`totals.no_email`) — so the three
              numbers on this screen sum to the panel's rather than double-counting the
              people the clause above just named.
            */}
            {excludedFromList > 0 && (
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_100}
              >
                {excludedFromList.toLocaleString()} more{' '}
                {excludedFromList === 1 ? 'person is' : 'people are'} in the
                audience — roster{' '}
                {excludedFromList === 1 ? 'member' : 'members'} with no account,
                reached only when Email is ticked with “{UNJOINED_SWITCH_TITLE}”
                on.
              </Text>
            )}
          </div>
        )}

        {error && (
          <div
            css={css`
              padding: 0 16px;
            `}
          >
            <Alert type='error' showIcon message={getErrorMessage(error)} />
          </div>
        )}

        {/* The grid runs edge-to-edge; that is what removeBodyPaddingX is for. */}
        {showSkeleton ? (
          <div
            css={css`
              padding: 0 16px;
            `}
          >
            <Skeleton active paragraph={{ rows: 8 }} title={false} />
          </div>
        ) : (
          /*
           * A FIXED height, not autoHeight. A full page of 50 rows rendered at its
           * natural height makes the modal taller than the viewport and pushes the
           * pager and Close button off-screen — the reader can see the list but not
           * page through it or leave. Bounded here, the rows scroll inside the grid
           * and the controls stay put.
           *
           * The shared DataGrid's wrapper is width-only, so the height has to reach
           * the AG Grid root through it.
           */
          <div
            css={css`
              height: ${GRID_HEIGHT}px;
              > div,
              .ag-root-wrapper {
                height: 100%;
              }
            `}
          >
            <DataGrid<SocialFeedRecipient>
              rowData={rows}
              columnDefs={columnDefs}
              loading={isFetching}
              popupParent={bodyRef.current ?? undefined}
              /*
               * NOT sortable, deliberately. This is AG Grid's client-side row model over
               * `rowData`, which is the current page of 50 — so a sort would reorder those
               * 50 rows and nothing else. On a competition of several thousand people the
               * top row after clicking "Name" is not the alphabetical first, and nothing on
               * screen would say so; a sort that silently means "sort this page" is worse
               * than no sort.
               *
               * Little is lost: the status chips already give the Status grouping and the
               * search box goes to the SERVER, which covers name and email lookup — the two
               * reasons to sort this list. Real sorting belongs in
               * `SocialFeedController::paginateRecipients` (a `usort` between the search
               * filter and the `array_slice`), not here.
               */
              defaultColDef={{ sortable: false, resizable: true }}
            />
          </div>
        )}

        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            flex-wrap: wrap;
            padding: 0 16px;
          `}
        >
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_100}
          >
            {pagination
              ? `Showing ${rows.length.toLocaleString()} of ${pagination.total.toLocaleString()}`
              : ''}
          </Text>

          <Pagination
            current={page}
            pageSize={PER_PAGE}
            total={pagination?.total ?? 0}
            showSizeChanger={false}
            onChange={setPage}
            size='small'
          />
        </div>
      </div>
    </BaseModal>
  )
}

export default RecipientsModal
